import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  UserCheck,
  UserX,
  Shield,
  RefreshCw,
  Download,
  AlertTriangle,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { userApi, User, UserSearchParams } from '@/api/users';
import { apiClient } from '@/api/client';
import toast from 'react-hot-toast';

const inputCls = 'w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';
const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1';
const checkboxCls = 'h-4 w-4 border-slate-300 text-primary-600 focus:ring-primary-500';
const smallLabelCls = 'block text-xs font-medium text-slate-500 mb-1';
const smallInputCls = 'w-full px-2 py-1.5 text-sm border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';

const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docsUser, setDocsUser] = useState<User | null>(null);
  const [userDocs, setUserDocs] = useState<any[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isDownloadingDoc, setIsDownloadingDoc] = useState<number | null>(null);
  const [isCreateJoint, setIsCreateJoint] = useState(false);
  const [isEditJoint, setIsEditJoint] = useState(false);
  const usersPerPage = 10;

  // Fetch users from API
  const fetchUsers = async (params: UserSearchParams = {}) => {
    try {
      setError(null);
      const defaultParams: UserSearchParams = {
        page: currentPage,
        page_size: usersPerPage,
        ...params
      };

      // Add filters
      if (roleFilter !== 'all') {
        defaultParams.role = roleFilter;
      }
      if (statusFilter !== 'all') {
        defaultParams.is_active = statusFilter === 'active';
      } else {
        // By default, only show active users (exclude deleted users)
        defaultParams.is_active = true;
      }
      if (searchTerm.trim()) {
        defaultParams.search = searchTerm.trim();
      }

      const response = await userApi.list(defaultParams);

      setUsers(response.users);
      setTotalUsers(response.total);
      setTotalPages(Math.ceil(response.total / usersPerPage));

    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcılar yüklenirken hata oluştu';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching users:', err);
    }
  };

  // Initial load
  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      try {
        await fetchUsers();
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [currentPage, roleFilter, statusFilter, searchTerm]);

  // Refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await fetchUsers();
      toast.success('Veriler güncellendi');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Toggle user status (activate/deactivate)
  const toggleUserStatus = async (userId: number) => {
    try {
      const updatedUser = await userApi.toggleStatus(userId);

      // Update local state
      setUsers(prev => prev.map(user =>
        user.id === userId ? updatedUser : user
      ));

      toast.success('Kullanıcı durumu güncellendi');

    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcı durumu güncellenirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error toggling user status:', err);
    }
  };

  // Delete user
  const deleteUser = async (userId: number) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await userApi.delete(userId);

      // Update local state
      setUsers(prev => prev.filter(user => user.id !== userId));
      setTotalUsers(prev => prev - 1);

      toast.success('Kullanıcı silindi');

    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcı silinirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error deleting user:', err);
    }
  };

  // Create new user
  const createUser = async (userData: any) => {
    setIsCreating(true);
    try {
      const newUser = await userApi.create(userData);

      // Update local state
      setUsers(prev => [newUser, ...prev]);
      setTotalUsers(prev => prev + 1);

      toast.success('Kullanıcı başarıyla oluşturuldu');
      setShowCreateModal(false);

    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcı oluşturulurken hata oluştu';
      toast.error(errorMessage);
      console.error('Error creating user:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // Update user
  const updateUser = async (userData: any) => {
    if (!editingUser) return;

    setIsUpdating(true);
    try {
      const updatedUser = await userApi.update(editingUser.id, userData);

      // Update local state
      setUsers(prev => prev.map(user =>
        user.id === editingUser.id ? updatedUser : user
      ));

      toast.success('Kullanıcı başarıyla güncellendi');
      setShowEditModal(false);
      setEditingUser(null);

    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcı güncellenirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error updating user:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle edit button click
  const handleEdit = (user: User) => {
    setEditingUser(user);
    setIsEditJoint(Boolean((user as any).is_joint_account));
    setShowEditModal(true);
  };

  const handleShowDocs = async (user: User) => {
    setDocsUser(user);
    setShowDocsModal(true);
    setIsLoadingDocs(true);
    try {
      const res = await apiClient.get(`/api/v1/pdf/admin/user-documents/${user.id}`);
      setUserDocs(res.data?.documents || []);
    } catch {
      toast.error('Dokumente konnten nicht geladen werden.');
      setUserDocs([]);
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const handleDownloadDoc = async (doc: any) => {
    setIsDownloadingDoc(doc.id);
    try {
      const response = await apiClient.get(`/api/v1/pdf/download/${doc.id}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download fehlgeschlagen.');
    } finally {
      setIsDownloadingDoc(null);
    }
  };

  // Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle filter change
  const handleFilterChange = (filterType: string, value: string) => {
    if (filterType === 'role') {
      setRoleFilter(value);
    } else if (filterType === 'status') {
      setStatusFilter(value);
    }
    setCurrentPage(1); // Reset to first page when filtering
  };

  const getRoleLabel = (role: string) => {
    const labels = {
      admin: 'Admin',
      staff: 'Personel',
      customer: 'Müşteri',
      callcenter_admin: 'Callcenter Admin'
    };
    return labels[role as keyof typeof labels] || role;
  };

  const getRoleColor = (role: string) => {
    const styles = {
      admin: 'bg-danger-50 text-danger-700 border-danger-200',
      staff: 'bg-primary-50 text-primary-700 border-primary-200',
      customer: 'bg-success-50 text-success-700 border-success-200',
      callcenter_admin: 'bg-gold-50 text-gold-700 border-gold-300'
    };
    return styles[role as keyof typeof styles] || 'bg-slate-50 text-slate-600 border-slate-200';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-16 w-16 border-2 border-primary-100 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-500">Kullanıcılar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 p-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-danger-600 mr-3" />
            <div>
              <h3 className="text-sm font-semibold text-danger-700">Hata</h3>
              <p className="text-sm text-danger-700">{error}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setError(null)}
            className="text-danger-600 hover:text-danger-800"
          >
            ✕
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Verwaltung</p>
          <h1 className="text-2xl font-serif font-medium text-slate-900">Kullanıcı Yönetimi</h1>
          <p className="text-slate-500 mt-1">Sistem kullanıcılarını yönetin ve düzenleyin</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={refreshData} disabled={isRefreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
          <Button variant="default" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Yeni Kullanıcı
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Toplam Kullanıcı</p>
              <p className="text-2xl font-serif font-medium text-slate-900 tabular-nums mt-1">{totalUsers}</p>
            </div>
            <div className="w-10 h-10 bg-primary-50 border border-primary-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 border-t-2 border-t-success-600 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Aktif Kullanıcı</p>
              <p className="text-2xl font-serif font-medium text-success-600 tabular-nums mt-1">
                {users.filter(u => u.is_active).length}
              </p>
            </div>
            <div className="w-10 h-10 bg-success-50 border border-success-100 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-success-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 border-t-2 border-t-danger-600 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Admin</p>
              <p className="text-2xl font-serif font-medium text-danger-600 tabular-nums mt-1">
                {users.filter(u => u.role === 'admin').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-danger-50 border border-danger-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-danger-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Müşteri</p>
              <p className="text-2xl font-serif font-medium text-gold-700 tabular-nums mt-1">
                {users.filter(u => u.role === 'customer').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-gold-50 border border-gold-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-gold-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-slate-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Kullanıcı ara (ad, email, telefon)..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <select
              value={roleFilter}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              className="px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">Tüm Roller</option>
              <option value="admin">Admin</option>
              <option value="staff">Personel</option>
              <option value="customer">Müşteri</option>
              <option value="callcenter_admin">Callcenter Admin</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Kullanıcı
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Kayıt Tarihi
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Son Giriş
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-gold-500 flex items-center justify-center text-white font-semibold">
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-slate-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-slate-500">{user.email}</div>
                        {user.phone && (
                          <div className="text-sm text-slate-500">{user.phone}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium border ${getRoleColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium border ${
                      user.is_active ? 'bg-success-50 text-success-700 border-success-200' : 'bg-danger-50 text-danger-700 border-danger-200'
                    }`}>
                      {user.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 tabular-nums">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 tabular-nums">
                    {user.last_login ? formatDate(user.last_login) : 'Hiç giriş yapmadı'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {user.role === 'customer' && (
                        <Button size="sm" variant="outline" onClick={() => handleShowDocs(user)} title="Dokumente anzeigen">
                          <FileText className="w-4 h-4 text-primary-600" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleEdit(user)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleUserStatus(user.id)}
                        className={user.is_active ? 'text-danger-600 hover:text-danger-800' : 'text-success-600 hover:text-success-800'}
                      >
                        {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteUser(user.id)}
                        className="text-danger-600 hover:text-danger-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-slate-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Önceki
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Sonraki
                </Button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-slate-700">
                    <span className="font-medium tabular-nums">{((currentPage - 1) * usersPerPage) + 1}</span>
                    {' '}-{' '}
                    <span className="font-medium tabular-nums">{Math.min(currentPage * usersPerPage, totalUsers)}</span>
                    {' '}arası, toplam{' '}
                    <span className="font-medium tabular-nums">{totalUsers}</span>
                    {' '}kullanıcı
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex -space-x-px" aria-label="Pagination">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Önceki
                    </Button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}

                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Sonraki
                    </Button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {users.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-serif font-medium text-slate-900 mb-2">Kullanıcı bulunamadı</h3>
          <p className="text-slate-500">Arama kriterlerinize uygun kullanıcı bulunamadı.</p>
        </div>
      )}

      {/* Edit User Modal (disabled duplicate) */}
      {false && showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-serif font-medium text-slate-900 mb-4">Kullanıcı Düzenle</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              updateUser({
                first_name: formData.get('first_name') as string,
                last_name: formData.get('last_name') as string,
                phone: formData.get('phone') as string || undefined,
                role: formData.get('role') as 'admin' | 'staff' | 'customer',
                is_active: formData.get('is_active') === 'true'
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Email</label>
                  <input
                    type="email"
                    value={editingUser?.email || ''}
                    disabled
                    className={`${inputCls} bg-slate-100 cursor-not-allowed`}
                  />
                  <p className="text-xs text-slate-500 mt-1">Email değiştirilemez</p>
                </div>
                <div>
                  <label className={labelCls}>Ad</label>
                  <input
                    type="text"
                    name="first_name"
                    defaultValue={editingUser?.first_name || ''}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Soyad</label>
                  <input
                    type="text"
                    name="last_name"
                    defaultValue={editingUser?.last_name || ''}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Telefon (Opsiyonel)</label>
                  <input
                    type="tel"
                    name="phone"
                    defaultValue={editingUser?.phone || ''}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Rol</label>
                  <select
                    name="role"
                    defaultValue={editingUser?.role || 'customer'}
                    required
                    className={inputCls}
                  >
                    <option value="customer">Müşteri</option>
                    <option value="staff">Personel</option>
                    <option value="admin">Admin</option>
                    <option value="callcenter_admin">Callcenter Admin</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Durum</label>
                  <select
                    name="is_active"
                    defaultValue={(editingUser?.is_active ?? true).toString()}
                    required
                    className={inputCls}
                  >
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  disabled={isUpdating}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Güncelleniyor...' : 'Güncelle'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-serif font-medium text-slate-900 mb-4">Yeni Kullanıcı Oluştur</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              createUser({
                email: formData.get('email') as string,
                password: formData.get('password') as string,
                first_name: formData.get('first_name') as string,
                last_name: formData.get('last_name') as string,
                phone: formData.get('phone') as string || undefined,
                role: formData.get('role') as 'admin' | 'staff' | 'customer',
                is_joint_account: isCreateJoint,
                ...(isCreateJoint ? {
                  partner_first_name: formData.get('partner_first_name') as string || undefined,
                  partner_last_name: formData.get('partner_last_name') as string || undefined,
                  partner_birth_date: formData.get('partner_birth_date') as string || undefined,
                  partner_national_id: formData.get('partner_national_id') as string || undefined,
                  partner_address: formData.get('partner_address') as string || undefined,
                  partner_city: formData.get('partner_city') as string || undefined,
                  partner_postal_code: formData.get('partner_postal_code') as string || undefined,
                  partner_country: formData.get('partner_country') as string || undefined,
                } : {})
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Şifre</label>
                  <input
                    type="password"
                    name="password"
                    required
                    placeholder="En az 8 karakter, büyük+küçük harf, rakam, özel karakter"
                    className={inputCls}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Şifre gereksinimleri: 8+ karakter, büyük harf (A-Z), küçük harf (a-z), rakam (0-9), özel karakter (!@#$)
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Ad</label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Soyad</label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Telefon (Opsiyonel)</label>
                  <input
                    type="tel"
                    name="phone"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Rol</label>
                  <select
                    name="role"
                    required
                    className={inputCls}
                  >
                    <option value="customer">Müşteri</option>
                    <option value="staff">Personel</option>
                    <option value="admin">Admin</option>
                    <option value="callcenter_admin">Callcenter Admin</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                  <input type="checkbox" id="create_joint" checked={isCreateJoint} onChange={e => setIsCreateJoint(e.target.checked)} className={checkboxCls}/>
                  <label htmlFor="create_joint" className="text-sm font-medium text-slate-700">Gemeinschaftskonto (Ortak Hesap)</label>
                </div>
                {isCreateJoint && (
                  <div className="space-y-3 p-3 bg-primary-50 border border-primary-100">
                    <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide">Mitinhaber Bilgileri</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={smallLabelCls}>Ad</label><input type="text" name="partner_first_name" className={smallInputCls}/></div>
                      <div><label className={smallLabelCls}>Soyad</label><input type="text" name="partner_last_name" className={smallInputCls}/></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={smallLabelCls}>Doğum Tarihi</label><input type="date" name="partner_birth_date" className={smallInputCls}/></div>
                      <div><label className={smallLabelCls}>National ID</label><input type="text" name="partner_national_id" className={smallInputCls}/></div>
                    </div>
                    <div><label className={smallLabelCls}>Adres</label><input type="text" name="partner_address" className={smallInputCls}/></div>
                    <div className="grid grid-cols-3 gap-2">
                      <input placeholder="Şehir" name="partner_city" className={smallInputCls}/>
                      <input placeholder="Posta Kodu" name="partner_postal_code" className={smallInputCls}/>
                      <input placeholder="Ülke" name="partner_country" className={smallInputCls}/>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowCreateModal(false); setIsCreateJoint(false); }}
                  disabled={isCreating}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating}
                >
                  {isCreating ? 'Oluşturuluyor...' : 'Oluştur'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-serif font-medium text-slate-900 mb-4">Kullanıcıyı Düzenle</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              updateUser({
                first_name: formData.get('first_name') as string,
                last_name: formData.get('last_name') as string,
                phone: formData.get('phone') as string || undefined,
                role: formData.get('role') as 'admin' | 'staff' | 'customer' | 'callcenter_admin',
                is_active: formData.get('is_active') === 'on',
                date_of_birth: formData.get('date_of_birth') as string || undefined,
                national_id: formData.get('national_id') as string || undefined,
                address: formData.get('address') as string || undefined,
                city: formData.get('city') as string || undefined,
                country: formData.get('country') as string || undefined,
                postal_code: formData.get('postal_code') as string || undefined,
                is_joint_account: isEditJoint,
                ...(isEditJoint ? {
                  partner_first_name: formData.get('partner_first_name') as string || undefined,
                  partner_last_name: formData.get('partner_last_name') as string || undefined,
                  partner_birth_date: formData.get('partner_birth_date') as string || undefined,
                  partner_national_id: formData.get('partner_national_id') as string || undefined,
                  partner_address: formData.get('partner_address') as string || undefined,
                  partner_city: formData.get('partner_city') as string || undefined,
                  partner_postal_code: formData.get('partner_postal_code') as string || undefined,
                  partner_country: formData.get('partner_country') as string || undefined,
                } : {})
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Ad</label>
                  <input
                    type="text"
                    name="first_name"
                    defaultValue={editingUser.first_name}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Soyad</label>
                  <input
                    type="text"
                    name="last_name"
                    defaultValue={editingUser.last_name}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Telefon (Opsiyonel)</label>
                  <input
                    type="tel"
                    name="phone"
                    defaultValue={editingUser.phone || ''}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Doğum Tarihi</label>
                  <input type="date" name="date_of_birth" defaultValue={(editingUser as any).date_of_birth || ''} className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>TC Kimlik / National ID</label>
                  <input type="text" name="national_id" defaultValue={(editingUser as any).national_id || ''} className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>Adres</label>
                  <input type="text" name="address" defaultValue={(editingUser as any).address || ''} className={inputCls}/>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input placeholder="Şehir" name="city" defaultValue={(editingUser as any).city || ''} className={inputCls}/>
                  <input placeholder="Ülke" name="country" defaultValue={(editingUser as any).country || ''} className={inputCls}/>
                  <input placeholder="Posta Kodu" name="postal_code" defaultValue={(editingUser as any).postal_code || ''} className={inputCls}/>
                </div>
                <div>
                  <label className={labelCls}>Rol</label>
                  <select
                    name="role"
                    defaultValue={editingUser.role}
                    required
                    className={inputCls}
                  >
                    <option value="customer">Müşteri</option>
                    <option value="staff">Personel</option>
                    <option value="admin">Admin</option>
                    <option value="callcenter_admin">Callcenter Admin</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    id="is_active"
                    defaultChecked={editingUser.is_active}
                    className={`mr-2 ${checkboxCls}`}
                  />
                  <label htmlFor="is_active" className="text-sm text-slate-700">
                    Aktif Kullanıcı
                  </label>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                  <input type="checkbox" id="edit_joint" checked={isEditJoint} onChange={e => setIsEditJoint(e.target.checked)} className={checkboxCls}/>
                  <label htmlFor="edit_joint" className="text-sm font-medium text-slate-700">Gemeinschaftskonto (Ortak Hesap)</label>
                </div>
                {isEditJoint && (
                  <div className="space-y-3 p-3 bg-primary-50 border border-primary-100">
                    <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide">Mitinhaber Bilgileri</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={smallLabelCls}>Ad</label><input type="text" name="partner_first_name" defaultValue={(editingUser as any).partner_first_name || ''} className={smallInputCls}/></div>
                      <div><label className={smallLabelCls}>Soyad</label><input type="text" name="partner_last_name" defaultValue={(editingUser as any).partner_last_name || ''} className={smallInputCls}/></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={smallLabelCls}>Doğum Tarihi</label><input type="date" name="partner_birth_date" defaultValue={(editingUser as any).partner_birth_date || ''} className={smallInputCls}/></div>
                      <div><label className={smallLabelCls}>National ID</label><input type="text" name="partner_national_id" defaultValue={(editingUser as any).partner_national_id || ''} className={smallInputCls}/></div>
                    </div>
                    <div><label className={smallLabelCls}>Adres</label><input type="text" name="partner_address" defaultValue={(editingUser as any).partner_address || ''} className={smallInputCls}/></div>
                    <div className="grid grid-cols-3 gap-2">
                      <input placeholder="Şehir" name="partner_city" defaultValue={(editingUser as any).partner_city || ''} className={smallInputCls}/>
                      <input placeholder="Posta Kodu" name="partner_postal_code" defaultValue={(editingUser as any).partner_postal_code || ''} className={smallInputCls}/>
                      <input placeholder="Ülke" name="partner_country" defaultValue={(editingUser as any).partner_country || ''} className={smallInputCls}/>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowEditModal(false); setIsEditJoint(false); }}
                  disabled={isUpdating}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Güncelleniyor...' : 'Güncelle'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {showDocsModal && docsUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary-600" />
                <div>
                  <h3 className="text-lg font-serif font-medium text-slate-900">
                    Dokumente — {docsUser.first_name} {docsUser.last_name}
                  </h3>
                  <p className="text-xs text-slate-500">{docsUser.email}</p>
                </div>
              </div>
              <button onClick={() => setShowDocsModal(false)} className="text-slate-400 hover:text-slate-600 text-xl font-bold leading-none">×</button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {isLoadingDocs ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-6 h-6 border-2 border-primary-100 border-t-primary-600 animate-spin" />
                </div>
              ) : userDocs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Noch keine Dokumente erstellt.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {userDocs.map((doc: any) => (
                    <div key={doc.id} className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors">
                      <div className="w-9 h-9 bg-primary-50 border border-primary-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-900">{doc.bank_name}</span>
                          {doc.kunden_nr && (
                            <span className="text-xs bg-white border border-slate-200 text-slate-500 px-2 py-0.5 font-mono">{doc.kunden_nr}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap text-xs text-slate-500">
                          <span className="tabular-nums">{doc.laufzeit} Mon.</span>
                          <span>·</span>
                          <span className="tabular-nums">{doc.zinssatz}% p.a.</span>
                          <span>·</span>
                          <span className="tabular-nums">{doc.anlagebetrag} €</span>
                          {doc.iban && <><span>·</span><span className="font-mono">{doc.iban.slice(0,8)}…</span></>}
                          <span>·</span>
                          <span className="tabular-nums">{new Date(doc.created_at).toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownloadDoc(doc)}
                        disabled={isDownloadingDoc === doc.id}
                        className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50 px-3 py-1.5 border border-primary-200 hover:bg-primary-50 transition-colors flex-shrink-0"
                      >
                        {isDownloadingDoc === doc.id
                          ? <div className="w-3.5 h-3.5 border-2 border-primary-100 border-t-primary-600 animate-spin" />
                          : <Download className="w-3.5 h-3.5" />
                        }
                        PDF
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 flex justify-end">
              <Button variant="outline" onClick={() => setShowDocsModal(false)}>Schließen</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
